import { createRouter, createWebHistory } from 'vue-router'
import AppShell from '@/layouts/AppShell.vue'
import Login from '@/views/Login.vue'
import Today from '@/views/Today.vue'
import Calendar from '@/views/Calendar.vue'
import store from '@/store'

/**
 * 라우트 meta 규약
 *   shell: 'tabs'   하단 탭바가 보이는 최상위 화면
 *          'stack'  뒤로가기 헤더가 붙는 하위 화면 (하단 탭바 숨김)
 *   tab:   BottomNav/SideNav 에서 활성 표시할 탭 key (components/nav/navItems.js)
 *   title: 상단바 제목. 없으면 탭 라벨을 쓴다.
 *
 * 하위 화면도 tab 을 갖는다. /tasks/123 에 있어도 '업무' 탭이 활성으로 보여야 하고,
 * 뒤로가기 히스토리가 없을 때 어디로 보낼지도 이 값으로 정한다.
 */
const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login
  },

  {
    path: '/',
    component: AppShell,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/today' },

      {
        path: 'today',
        name: 'Today',
        component: Today,
        meta: { shell: 'tabs', tab: 'today', title: '홈' }
      },

      // Phase 1 에서는 기존 경로 형태를 유지한다.
      // Calendar.vue 가 syncCalendarRoute() 에서 `/calendar/${view}` 로 replace 하므로
      // 지금 쿼리 규약으로 바꾸면 리다이렉트와 서로를 밀어내며 루프가 된다.
      // 통합 캘린더로 재작성하는 Phase 2 에서 /calendar?view= 로 옮긴다.
      { path: 'calendar', redirect: '/calendar/month' },
      {
        path: 'calendar/:view',
        name: 'Calendar',
        component: Calendar,
        props: true,
        meta: { shell: 'tabs', tab: 'calendar', title: '캘린더' }
      },

      {
        path: 'tasks',
        name: 'Tasks',
        component: () => import('@/views/Tasks.vue'),
        meta: { shell: 'tabs', tab: 'tasks', title: '업무' }
      },
      {
        path: 'tasks/:id',
        name: 'TaskDetail',
        component: () => import('@/views/tasks/TaskDetail.vue'),
        props: true,
        meta: { shell: 'stack', tab: 'tasks', title: '업무 상세' }
      },

      {
        path: 'reports',
        name: 'ReportList',
        component: () => import('@/views/reports/ReportList.vue'),
        meta: { shell: 'tabs', tab: 'reports', title: '업무보고' }
      },
      {
        /*
         * 결재함은 **하단 탭바에는 없고 좌측 레일에만** 있다(navItems.js surface:'rail').
         * 12명 중 9명은 결재할 게 없고, 빈 탭은 5칸짜리 바에서 영구 사표다.
         * 반대로 세로 레일은 한 줄 더 놓을 자리가 있고, 결재는 관리자가
         * 데스크톱에서 처리하는 일이라 거기 있는 편이 맞다.
         * 모바일에서는 홈의 결재 카드와 보고 목록의 '결재함' 버튼으로 들어간다.
         */
        path: 'approvals',
        name: 'Approvals',
        component: () => import('@/views/Approvals.vue'),
        meta: { shell: 'stack', tab: 'approvals', title: '결재함' }
      },
      {
        path: 'reports/compose',
        name: 'ReportCompose',
        component: () => import('@/views/reports/ReportCompose.vue'),
        meta: { shell: 'stack', tab: 'reports', title: '보고 작성' }
      },
      {
        path: 'reports/:id/edit',
        name: 'ReportEdit',
        component: () => import('@/views/reports/ReportCompose.vue'),
        props: true,
        meta: { shell: 'stack', tab: 'reports', title: '보고 수정' }
      },
      {
        path: 'reports/:id',
        name: 'ReportDetail',
        component: () => import('@/views/reports/ReportDetail.vue'),
        props: true,
        meta: { shell: 'stack', tab: 'reports', title: '보고 상세' }
      },

      // ---- 영업·상담 ----
      // 영업 홈은 탭 화면(shell:'tabs')이다. 하단 탭바에 '영업' 칸은 없지만(navItems.js),
      // 매장 사이를 오가며 하단 탭으로 홈 · 캘린더에 바로 갈 수 있어야 해서 탭바를 남긴다.
      {
        path: 'sales',
        name: 'Sales',
        component: () => import('@/views/sales/SalesHome.vue'),
        meta: { shell: 'tabs', tab: 'sales', title: '영업' }
      },
      {
        path: 'sales/stores/:id',
        name: 'SalesStoreDetail',
        component: () => import('@/views/sales/SalesStoreDetail.vue'),
        props: true,
        meta: { shell: 'stack', tab: 'sales', title: '매장 상세' }
      },
      {
        path: 'sales/visits/:visitId',
        name: 'SalesVisitDetail',
        component: () => import('@/views/sales/SalesVisitDetail.vue'),
        props: true,
        meta: { shell: 'stack', tab: 'sales', title: '방문 · 답사 보고서' }
      },
      {
        path: 'sales/openbiz',
        name: 'SalesOpenbiz',
        component: () => import('@/views/sales/SalesOpenbiz.vue'),
        meta: { shell: 'stack', tab: 'sales', title: '오픈바이징' }
      },
      {
        path: 'sales/questions',
        name: 'SalesQuestionSets',
        component: () => import('@/views/sales/SalesQuestionSets.vue'),
        meta: { shell: 'stack', tab: 'sales', title: '질문지 관리' }
      },
      {
        path: 'sales/library',
        name: 'SalesLibrary',
        component: () => import('@/views/sales/SalesLibrary.vue'),
        meta: { shell: 'stack', tab: 'sales', title: '문서함' }
      },
      {
        path: 'sales/activity',
        name: 'SalesActivityLog',
        component: () => import('@/views/sales/SalesActivityLog.vue'),
        meta: { shell: 'stack', tab: 'sales', title: '변경 기록' }
      },
      // ---- 인사관리 ----
      // 직원 · 청소팀 면담 일지. 영업과 관계없다. 중요관리자 이상(grade 3 · 4) + 인사 권한(hr_access)을 켜 준 사람만 — 서버(routes/hr.js)가 막고,
      // 여기서는 권한 없는 사람이 주소를 쳐서 빈 화면을 보지 않게만 한다(requiresHr).
      // 탭 화면인 이유는 영업과 같다 — 하단 탭에 칸은 없지만 홈 · 캘린더로 바로 가야 한다.
      {
        path: 'hr',
        name: 'Hr',
        component: () => import('@/views/hr/HrHome.vue'),
        meta: { shell: 'tabs', tab: 'hr', title: '인사관리', requiresHr: true }
      },
      // ---- 회의 ----
      // 거래처 미팅 기록 · 관리자 회의록 · 후속 할 일. 시스템에 들어오는 사람 모두 쓴다(routes/meetings.js).
      // 업무 설명에 적힌 링크(/meetings?tab=partner&p=12)로 들어오므로 탭 안 위치는 전부 쿼리다.
      {
        path: 'meetings',
        name: 'Meetings',
        component: () => import('@/views/meetings/MeetingsHome.vue'),
        meta: { shell: 'tabs', tab: 'meetings', title: '회의' }
      },
      {
        // 업무 바로가기. 더보기(모바일) · 레일의 '전체'(데스크톱) · 태블릿 레일 아이콘에서 들어온다.
        path: 'links',
        name: 'QuickLinks',
        component: () => import('@/views/QuickLinks.vue'),
        meta: { shell: 'stack', tab: 'links', title: '바로가기' }
      },

      {
        path: 'more',
        name: 'More',
        component: () => import('@/views/More.vue'),
        meta: { shell: 'tabs', tab: 'more', title: '더보기' }
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/Profile.vue'),
        meta: { shell: 'stack', tab: 'more', title: '프로필' }
      },
      {
        // 지금까지 어떤 메뉴에서도 닿을 수 없던 화면. 더보기에서 진입한다.
        path: 'locations',
        name: 'LocationManagement',
        component: () => import('@/views/LocationManagement.vue'),
        meta: { shell: 'stack', tab: 'more', title: '장소 관리' }
      },
      {
        path: 'admin',
        name: 'Admin',
        component: () => import('@/views/Admin.vue'),
        meta: { shell: 'stack', tab: 'more', title: '관리자', requiresAdmin: true }
      },
      {
        path: 'admin/org',
        name: 'OrgChart',
        component: () => import('@/views/admin/OrgChart.vue'),
        meta: { shell: 'stack', tab: 'more', title: '조직도', requiresAdmin: true }
      }
    ]
  },

  // ---- 레거시 경로 호환 ----
  // 쿼리를 반드시 보존한다. 카카오 콜백이 /dashboard?login=success 로 돌아오므로
  // 여기서 query 를 떨어뜨리면 로그인 직후 인증 확인이 실행되지 않는다.
  { path: '/dashboard', redirect: (to) => ({ path: '/today', query: to.query }) },
  { path: '/todos/:view?', redirect: () => ({ path: '/tasks' }) },

  // 알 수 없는 경로
  { path: '/:pathMatch(.*)*', redirect: '/today' }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    // 같은 화면에서 필터/뷰만 바뀐 경우(쿼리 변경)에는 스크롤을 유지한다
    if (to.path === from.path) return
    return { top: 0 }
  }
})

/*
 * 권한 판단은 스토어 게터를 그대로 쓴다(store/modules/auth.js). 여기서 localStorage 를 다시 읽고
 * 조건을 한 벌 더 적어 두면 두 곳이 어긋난다 — 스토어의 user 는 localStorage 로 시작하고 SET_USER 로만 바뀐다.
 * 화면 권한은 "주소를 쳐서 빈 화면을 보지 않게" 하는 용도일 뿐이고, 실제 차단은 서버가 한다.
 */
router.beforeEach(async (to) => {
  // 카카오 로그인 직후: 서버가 세션을 막 만들었고 스토어(localStorage)에는 아직 user 가 없다.
  // 경로가 아니라 이 쿼리로 판단한다 — /dashboard 는 /today 로 리다이렉트되므로 가드에 올 때 to.path 는 이미 /today 다.
  // 화면에 들어가기 **전에** 사용자를 받아 둔다. 예전에는 일단 통과시키고 홈 화면이 뒤늦게 확인했는데,
  // 그 사이 셸이 사용자 없이 그려져 배지가 비고, 홈 말고 다른 경로로 돌아오면 아무도 확인하지 않았다.
  if (to.query.login === 'success') {
    try {
      await store.dispatch('checkAuth')
    } catch (e) {
      return '/login'
    }
    const query = { ...to.query }
    delete query.login
    return { path: to.path, query, hash: to.hash, replace: true }
  }

  const authed = store.getters.isAuthenticated
  if (to.matched.some((r) => r.meta.requiresAuth)) {
    if (!authed) return '/login'
    if (to.matched.some((r) => r.meta.requiresAdmin) && !store.getters.isAdmin) return '/today'
    if (to.matched.some((r) => r.meta.requiresHr) && !store.getters.canHr) return '/today'
    return true
  }

  if (to.path === '/login' && authed) return '/today'
  return true
})

export default router
